/**
 * Percurso da atividade (GPS) em SVG próprio, sem biblioteca, sobre o mapa de fundo do OpenStreetMap.
 * O fundo é opcional ("Mostrar mapa de fundo"): ao ligá-lo, o navegador baixa as imagens (tiles) da região
 * direto de tile.openstreetmap.org. Desligado, o percurso é só o traçado e nada sai do servidor da assessoria.
 * Projeção Web Mercator (a mesma dos tiles), norte para cima; o traçado é colorido pelo ritmo
 * (azul = mais lento, vermelho = mais rápido) e um seletor percorre o trajeto mostrando km, tempo, ritmo e FC.
 */
import { useMemo, useState } from "react";
import { svgScaleStyle, useSvgScale } from "../lib/useSvgScale";
import type { ActivityStream } from "../api/types";
import { formatDuration, formatPace } from "../lib/format";
import { LOCALE, t, tj } from "../i18n";

const W = 640;
const H = 380;
const PAD = 34;
const BINS = 6;
const TILE = 256;
const TILE_URL = (z: number, x: number, y: number) => `https://tile.openstreetmap.org/${z}/${x}/${y}.png`;
const PREF_KEY = "eapace.routemap.basemap";

/** Pixel global (Web Mercator) no nível de zoom z. */
function world(lat: number, lon: number, z: number): [number, number] {
  const n = TILE * 2 ** z;
  const sin = Math.sin((Math.max(-85.0511, Math.min(85.0511, lat)) * Math.PI) / 180);
  return [((lon + 180) / 360) * n, (0.5 - Math.log((1 + sin) / (1 - sin)) / (4 * Math.PI)) * n];
}

function readPref(): boolean {
  try {
    return localStorage.getItem(PREF_KEY) !== "off";
  } catch {
    return true;
  }
}

interface Pt {
  lat: number;
  lon: number;
  t: number;
  d: number | null;
  hr: number | null;
  x: number;
  y: number;
  pace: number | null;
}

function build(stream: ActivityStream) {
  const { t: tp, d, hr, lat, lon } = stream.data;
  if (!lat || !lon) return null;
  const raw: Omit<Pt, "x" | "y" | "pace">[] = [];
  for (let i = 0; i < tp.length; i++) {
    const la = lat[i];
    const lo = lon[i];
    if (la == null || lo == null || Math.abs(la) > 90 || Math.abs(lo) > 180) continue;
    raw.push({ lat: la, lon: lo, t: tp[i], d: d?.[i] ?? null, hr: hr?.[i] ?? null });
  }
  if (raw.length < 5) return null;
  // Maior zoom (até 17) em que o percurso cabe no quadro com os tiles a pelo menos ~60 % do tamanho nativo.
  const fit = (z: number) => {
    const w = raw.map((p) => world(p.lat, p.lon, z));
    const xs = w.map((v) => v[0]);
    const ys = w.map((v) => v[1]);
    const minX = Math.min(...xs);
    const maxX = Math.max(...xs);
    const minY = Math.min(...ys);
    const maxY = Math.max(...ys);
    const spanX = Math.max(maxX - minX, 1);
    const spanY = Math.max(maxY - minY, 1);
    const scale = Math.min((W - 2 * PAD) / spanX, (H - 2 * PAD) / spanY);
    return { w, minX, minY, spanX, spanY, scale };
  };
  let zoom = 17;
  let f = fit(zoom);
  while (zoom > 2 && f.scale < 0.6) {
    zoom -= 1;
    f = fit(zoom);
  }
  const cx = f.minX + f.spanX / 2;
  const cy = f.minY + f.spanY / 2;
  const left = cx - W / 2 / f.scale; // canto superior esquerdo do quadro, em pixels globais
  const top = cy - H / 2 / f.scale;
  const latMid = raw.reduce((acc, p) => acc + p.lat, 0) / raw.length;
  const metersPerPx = (40_075_016.686 * Math.cos((latMid * Math.PI) / 180)) / (TILE * 2 ** zoom) / f.scale; // por pixel da tela
  const pts: Pt[] = raw.map((p, i) => ({
    ...p,
    x: (f.w[i][0] - left) * f.scale,
    y: (f.w[i][1] - top) * f.scale,
    pace: null,
  }));
  const tiles: { key: string; url: string; x: number; y: number; size: number }[] = [];
  const nTiles = 2 ** zoom;
  for (let ty = Math.floor(top / TILE); ty <= Math.floor((top + H / f.scale) / TILE); ty++) {
    for (let tx = Math.floor(left / TILE); tx <= Math.floor((left + W / f.scale) / TILE); tx++) {
      if (ty < 0 || ty >= nTiles) continue;
      const wrapX = ((tx % nTiles) + nTiles) % nTiles;
      tiles.push({
        key: `${zoom}/${tx}/${ty}`,
        url: TILE_URL(zoom, wrapX, ty),
        x: (tx * TILE - left) * f.scale,
        y: (ty * TILE - top) * f.scale,
        size: TILE * f.scale,
      });
    }
  }
  // ritmo por ponto: janela de ~30 s (igual aos gráficos)
  let j = 0;
  for (let i = 1; i < pts.length; i++) {
    while (j < i - 1 && pts[i].t - pts[j + 1].t >= 30) j++;
    const a = pts[i];
    const b = pts[j];
    if (a.d != null && b.d != null && a.d - b.d > 5 && a.t > b.t) {
      const pace = ((a.t - b.t) / (a.d - b.d)) * 1000;
      if (pace >= 150 && pace <= 900) a.pace = pace;
    }
  }
  const paces = pts.map((p) => p.pace).filter((v): v is number => v != null).sort((x, y) => x - y);
  const lo = paces[Math.floor(paces.length * 0.05)] ?? 0;
  const hi = paces[Math.ceil(paces.length * 0.95) - 1] ?? 0;
  return { pts, tiles, metersPerPx, lo, hi, hasPace: paces.length >= 5 && hi - lo >= 5 };
}

function binOf(p: Pt, lo: number, hi: number): number {
  if (p.pace == null) return 0;
  const f = (Math.min(hi, Math.max(lo, p.pace)) - lo) / (hi - lo || 1); // 0 = mais rápido
  return Math.min(BINS - 1, Math.floor((1 - f) * BINS)); // 0 = mais lento ... BINS-1 = mais rápido
}

function niceMeters(maxM: number): number {
  const steps = [50, 100, 200, 250, 500, 1000, 2000, 2500, 5000, 10000];
  return [...steps].reverse().find((s) => s <= maxM) ?? 50;
}

export function RouteMap({ stream }: { stream: ActivityStream }) {
  const m = useMemo(() => build(stream), [stream]);
  const [i, setI] = useState<number | null>(null);
  const [byPace, setByPace] = useState(true);
  const [basemap, setBasemap] = useState(readPref);
  const [svgRef, k] = useSvgScale(W);   // v0.18.0: textos e marcadores com tamanho fixo na tela
  if (!m) return null;
  const { pts, tiles, metersPerPx, lo, hi, hasPace } = m;
  const colorize = byPace && hasPace;

  // trechos contíguos com a mesma cor viram um único <path>
  const runs: { bin: number; d: string }[] = [];
  let cur: { bin: number; d: string } | null = null;
  for (let n = 1; n < pts.length; n++) {
    const bin = colorize ? binOf(pts[n], lo, hi) : 0;
    const seg = `L${pts[n].x.toFixed(1)},${pts[n].y.toFixed(1)}`;
    if (cur && cur.bin === bin) cur.d += seg;
    else {
      cur = { bin, d: `M${pts[n - 1].x.toFixed(1)},${pts[n - 1].y.toFixed(1)}${seg}` };
      runs.push(cur);
    }
  }
  const all = pts.map((p, n) => `${n ? "L" : "M"}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join("");

  const first = pts[0];
  const last = pts[pts.length - 1];
  const loop = Math.hypot(last.x - first.x, last.y - first.y) * metersPerPx < 60; // fim a menos de 60 m do início
  const totalKm = (last.d ?? 0) / 1000;
  const kmStep = totalKm > 30 ? 10 : totalKm > 15 ? 5 : totalKm > 6 ? 2 : 1;
  const kmMarks: { km: number; x: number; y: number }[] = [];
  for (let km = kmStep; km <= totalKm - 0.3; km += kmStep) {
    const idx = pts.findIndex((p) => p.d != null && p.d >= km * 1000);
    if (idx > 0) kmMarks.push({ km, x: pts[idx].x, y: pts[idx].y });
  }
  const barM = niceMeters(W * 0.22 * metersPerPx);
  const barPx = barM / metersPerPx;
  const sel = i != null ? pts[Math.min(i, pts.length - 1)] : null;

  return (
    <div className="route-map">
      <div className="route-map-tools">
        <label className="route-map-toggle">
          <input type="checkbox" checked={byPace} onChange={(e) => setByPace(e.target.checked)} disabled={!hasPace} />
          {t("Colorir pelo ritmo")}
        </label>
        <label className="route-map-toggle">
          <input
            type="checkbox"
            checked={basemap}
            onChange={(e) => {
              setBasemap(e.target.checked);
              try {
                localStorage.setItem(PREF_KEY, e.target.checked ? "on" : "off");
              } catch {
                /* preferência só desta sessão */
              }
            }}
          />
          {t("Mostrar mapa de fundo")}
        </label>
        {colorize && (
          <div className="route-map-legend" aria-label={t("Legenda de ritmo")}>
            <span>{formatPace(hi)}</span>
            <span className="route-map-ramp" aria-hidden="true">
              {Array.from({ length: BINS }, (_, b) => (
                <i key={b} className={`rm-p${b}`} />
              ))}
            </span>
            <span>{formatPace(lo)}</span>
          </div>
        )}
      </div>

      <svg
        ref={svgRef}
        style={svgScaleStyle(k)}
        viewBox={`0 0 ${W} ${H}`}
        role="img"
        aria-label={t("Percurso da atividade no mapa, com {v} km", { v: totalKm.toLocaleString(LOCALE, { maximumFractionDigits: 1 }) })}
      >
        <defs>
          <clipPath id="route-clip">
            <rect x={0} y={0} width={W} height={H} rx={10} />
          </clipPath>
        </defs>
        <rect x={0} y={0} width={W} height={H} className="route-bg" rx={10} />
        {basemap && (
          <g clipPath="url(#route-clip)" className="route-tiles">
            {tiles.map((tp) => (
              <image key={tp.key} href={tp.url} x={tp.x} y={tp.y} width={tp.size + 0.5} height={tp.size + 0.5} />
            ))}
          </g>
        )}
        <path d={all} className="route-casing" />
        {runs.map((r, n) => (
          <path key={n} d={r.d} className={`route-line ${colorize ? `rm-s${r.bin}` : "rm-one"}`} />
        ))}
        {kmMarks.map((km) => (
          <g key={km.km}>
            <circle cx={km.x} cy={km.y} r={10 * k} className="route-km" />
            <text x={km.x} y={km.y + 4 * k} textAnchor="middle" className="route-km-text">
              {km.km}
            </text>
          </g>
        ))}
        <circle cx={first.x} cy={first.y} r={7} className="route-start" />
        <text
          x={first.x + (first.x > W * 0.7 ? -11 : 11) * k}
          y={first.y - 9 * k}
          textAnchor={first.x > W * 0.7 ? "end" : "start"}
          className="route-label"
        >
          {loop ? t("Início/Fim") : t("Início")}
        </text>
        {!loop && (
          <>
            <circle cx={last.x} cy={last.y} r={7} className="route-end" />
            <text
              x={last.x + (last.x > W * 0.7 ? -11 : 11) * k}
              y={last.y - 9 * k}
              textAnchor={last.x > W * 0.7 ? "end" : "start"}
              className="route-label"
            >
              {t("Fim")}
            </text>
          </>
        )}
        {sel && (
          <g>
            <circle cx={sel.x} cy={sel.y} r={9} className="route-cursor" />
            <circle cx={sel.x} cy={sel.y} r={3} className="route-cursor-dot" />
          </g>
        )}
        <g transform={`translate(${PAD - 14}, ${H - 16})`} className="route-scale">
          <line x1={0} x2={barPx} y1={0} y2={0} />
          <line x1={0} x2={0} y1={-4} y2={4} />
          <line x1={barPx} x2={barPx} y1={-4} y2={4} />
          <text x={barPx + 6} y={4}>
            {barM >= 1000 ? `${barM / 1000} km` : `${barM} m`}
          </text>
        </g>
        {basemap && (
          <text x={W - 8} y={H - 6} textAnchor="end" className="route-attrib">
            © OpenStreetMap
          </text>
        )}
        <g transform={`translate(${W - 26}, 30)`} className="route-north" aria-hidden="true">
          <path d="M0,-14 L6,6 L0,2 L-6,6 Z" />
          <text x={0} y={20} textAnchor="middle">
            N
          </text>
        </g>
      </svg>

      <div className="route-map-cursor">
        <label htmlFor="route-pos">{t("Posição no percurso")}</label>
        <input
          id="route-pos"
          type="range"
          min={0}
          max={pts.length - 1}
          value={i ?? 0}
          onChange={(e) => setI(Number(e.target.value))}
          aria-valuetext={sel ? `${((sel.d ?? 0) / 1000).toLocaleString(LOCALE, { maximumFractionDigits: 2 })} km` : undefined}
        />
        <p className="route-map-readout" aria-live="polite">
          {sel ? (
            <>
              <strong>{((sel.d ?? 0) / 1000).toLocaleString(LOCALE, { maximumFractionDigits: 2 })} km</strong>
              {" · "}{t("tempo {v}", { v: formatDuration(Math.round(sel.t)) })}
              {sel.pace != null && <> · {t("ritmo {v}", { v: formatPace(sel.pace) })}</>}
              {sel.hr != null && <> · {t("FC {v} bpm", { v: Math.round(sel.hr) })}</>}
            </>
          ) : (
            t("Mova o seletor para ver km, tempo, ritmo e FC em cada ponto.")
          )}
        </p>
      </div>
      <p className="muted route-map-note">
        {basemap ? (
          <>
            {tj("Mapa de fundo © {link}. Para exibi-lo, o seu navegador pede à OpenStreetMap as imagens da região do percurso; o traçado em si não é enviado. Desligue o mapa de fundo para não enviar nenhuma informação de local.", {
              link: (
                <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener noreferrer">
                  {t("colaboradores do OpenStreetMap")}
                </a>
              ),
            })}
          </>
        ) : (
          t("Mapa de fundo desligado: nenhuma informação de local é enviada a serviços externos.")
        )}{" "}
        {t("Percursos de treinos reais podem revelar onde a pessoa mora ou trabalha; compartilhe com cuidado.")}
      </p>
    </div>
  );
}
